import { t } from "@travily/i18n";
import memberIllustration from "@ui/assets/illustrations/member-animated.webp";
import { AvatarPicker, type AvatarPickerPreset, type Selection } from "@ui/components/AvatarPicker";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";

const COLOR = "green" as const;

export interface UserAvatarStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** The current/default avatar tile, rendered as the first selectable option. */
  defaultTile: ReactNode;
  /** Extra preset tiles shown before the picker's built-in presets (e.g. "use current photo"). */
  leadingPresets: readonly AvatarPickerPreset[];
  /** Controlled picker selection. */
  selection: Selection;
  /** Preview URL for the upload slot (blob URL or existing image URL). */
  uploadPreview: string | null;
  /** Validation message for the avatar field. */
  errorMessage?: ReactNode;
  /** Called when the picker selection changes. */
  onSelectionChange: (selection: Selection) => void;
  /** Called when a file is chosen for upload. */
  onFileSelect: (file: File) => void;
  /** Called when the uploaded file is removed. */
  onRemove: () => void;
}

export function UserAvatarStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  defaultTile,
  leadingPresets,
  selection,
  uploadPreview,
  errorMessage,
  onSelectionChange,
  onFileSelect,
  onRemove,
}: UserAvatarStepViewProps) {
  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="!h-[168px] !w-[112px]" src={memberIllustration} />}
        heading={t("components.views.user.onboarding.avatar_view.title")}
        helper={t("components.views.user.onboarding.avatar_view.description")}
      >
        <div className="w-full max-w-md lg:max-w-lg">
          <AvatarPicker
            defaultTile={defaultTile}
            errorMessage={errorMessage}
            // Character presets need backend avatar_kind support the creator op doesn't have yet,
            // so the wizard offers upload + current photo + initial only.
            includeCharacterPresets={false}
            isLabelHidden
            label={t("components.views.user.onboarding.avatar_view.label")}
            leadingPresets={leadingPresets}
            onFileSelect={onFileSelect}
            onRemove={onRemove}
            onSelectionChange={onSelectionChange}
            selection={selection}
            uploadPreview={uploadPreview}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
